/**
 * Shared Solana JSON-RPC URL (chart supply, native balances).
 *
 * The default is `/sol-rpc` — a same-origin path that:
 *   - On localhost: Vite dev server proxies to api.mainnet-beta.solana.com
 *   - On Vercel:    rewrite rule proxies to api.mainnet-beta.solana.com
 * This avoids CORS issues that public RPCs have with browser origins.
 *
 * Override via `VITE_SOLANA_RPC_URL` for self-hosters with a paid RPC (Helius, Triton, etc.)
 */

const LS_RPC_URL = "unt_solana_rpc_url_v1";
const LS_HELIOS_KEY = "unt_helius_api_key_v1";
const LS_SOLSCAN_KEY = "unt_solscan_api_key_v1";

import { getBackendConfig } from "./pumpPortalConfig";

export function getStoredSolanaRpcUrl(): string {
  if (typeof window === "undefined") return "";
  try { return localStorage.getItem(LS_RPC_URL)?.trim() ?? ""; } catch { return ""; }
}

export function setStoredSolanaRpcUrl(url: string): void {
  if (typeof window === "undefined") return;
  try {
    const u = url.trim();
    if (u) localStorage.setItem(LS_RPC_URL, u);
    else localStorage.removeItem(LS_RPC_URL);
  } catch { /* ignore */ }
}

export function getStoredHeliosKey(): string {
  if (typeof window === "undefined") return "";
  try {
    const localVal = localStorage.getItem(LS_HELIOS_KEY)?.trim() ?? "";
    if (localVal) return localVal;
    const backend = getBackendConfig();
    if (backend?.heliusApiKeyConfigured) return "backend-configured";
    return "";
  } catch { return ""; }
}

export function setStoredHeliosKey(key: string): void {
  if (typeof window === "undefined") return;
  try {
    const k = key.trim();
    if (k) localStorage.setItem(LS_HELIOS_KEY, k);
    else localStorage.removeItem(LS_HELIOS_KEY);
  } catch { /* ignore */ }
}

export function getStoredSolscanKey(): string {
  if (typeof window === "undefined") return "";
  try {
    const localVal = localStorage.getItem(LS_SOLSCAN_KEY)?.trim() ?? "";
    if (localVal) return localVal;
    const backend = getBackendConfig();
    if (backend?.solscanApiKeyConfigured) return "backend-configured";
    return "";
  } catch { return ""; }
}

export function setStoredSolscanKey(key: string): void {
  if (typeof window === "undefined") return;
  try {
    const k = key.trim();
    if (k) localStorage.setItem(LS_SOLSCAN_KEY, k);
    else localStorage.removeItem(LS_SOLSCAN_KEY);
  } catch { /* ignore */ }
}

export function getSolscanApiKey(): string {
  const stored = getStoredSolscanKey();
  if (stored) return stored;
  const backend = getBackendConfig();
  if (backend?.solscanApiKeyConfigured) return "backend-configured";
  return import.meta.env?.VITE_SOLSCAN_API_KEY || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJjcmVhdGVkQXQiOjE3ODA1Njk3ODgzNDUsImVtYWlsIjoiZ2l0b21laGJyYW5zb25AZ21haWwuY29tIiwiYWN0aW9uIjoidG9rZW4tYXBpIiwiYXBpVmVyc2lvbiI6InYyIiwiaWF0IjoxNzgwNTY5Nzg4fQ.xCR9xmUyFfEsvp2u8r1daGn1cLlbqjNX89E8mwGpZ_0";
}

export function getHeliusRpcUrl(): string {
  const stored = getStoredHeliosKey();
  if (stored === "backend-configured") {
    return `/helius-rpc`;
  }
  const key = stored || "c78141ce-045d-48ab-9847-405100d54a14"; // Fallback key
  return `https://mainnet.helius-rpc.com/?api-key=${key}`;
}

export function getSolanaRpcUrl(): string {
  const stored = getStoredSolanaRpcUrl();
  if (stored.startsWith("http")) return stored;
  const u = import.meta.env?.VITE_SOLANA_RPC_URL;
  if (typeof u === "string" && u.startsWith("http")) return u;
  return "/sol-rpc";
}

/** POST a JSON-RPC body to the configured Solana RPC (Prioritizes Helius, falls back to standard/public RPC). */
export async function fetchSolanaRpc(body: Record<string, unknown>): Promise<Response> {
  const heliusUrl = getHeliusRpcUrl();
  const standardUrl = getSolanaRpcUrl();
  
  // URL priority list
  const rpcUrls = [heliusUrl, standardUrl, "https://api.mainnet-beta.solana.com"];
  
  let attempts = 0;
  const maxAttempts = rpcUrls.length;
  let lastErr: any = null;

  while (attempts < maxAttempts) {
    const url = rpcUrls[attempts];
    const ac = new AbortController();
    const t = setTimeout(() => ac.abort(), 8000);
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        signal: ac.signal,
      });

      if (!res.ok && res.status !== 400 && attempts < maxAttempts - 1) { // 400 means valid JSON-RPC error usually, but 429/500 we retry
        attempts++;
        continue;
      }
      return res;
    } catch (err) {
      lastErr = err;
      if (attempts < maxAttempts - 1) {
        attempts++;
        continue;
      }
      throw err;
    } finally {
      clearTimeout(t);
    }
  }
  throw lastErr || new Error("RPC failed after retries");
}
